// Onboarding documentation: what each memory file/group is for, written for
// non-technical teammates. Used by the in-app help panel and per-file hints.

export const GROUP_DOCS = {
  semantic: {
    title: "Semantic — what the agent KNOWS about the client",
    body:
      "Stable knowledge about the brand and its site: who the client is, what they sell, how their site is built. It changes rarely and provides context for every scan.",
  },
  procedural: {
    title: "Procedural — what the agent MUST DO",
    body:
      "The rules to apply: what to flag, what NOT to flag, what the client values, and the site-specific skills.",
  },
  episodic: {
    title: "Episodic — what HAPPENED",
    body:
      "The scan-by-scan history: published findings, discards and their reasons, summaries. This is the raw material that feeds the rules.",
  },
  config: {
    title: "Config & mapping",
    body:
      "Technical settings: the mapping to the site's backend identifier and the scanned scopes.",
  },
  runtime: {
    title: "Runtime",
    body:
      "Execution artifacts generated by the machine (crons, events, fan-outs). Rarely edited by hand.",
  },
  other: {
    title: "Other",
    body: "Files not classified in the categories above.",
  },
};

// Ordered list of matchers — first match wins.
const FILE_DOCS = [
  {
    test: (rel) => rel === "BUSINESS.md" || rel.startsWith("business/"),
    title: "BUSINESS.md",
    role:
      "Describes who the client is: the brand, what they sell, their product taxonomy, their priorities and their \"voice\" (tone, focus). This is the business context the agent reads before each scan.",
    edit:
      "Editable. Enrich it whenever you learn durable context about the client (positioning, product ranges, constraints).",
  },
  {
    test: (rel) => rel === "PREFERENCES.md",
    title: "PREFERENCES.md",
    role:
      "The client's rules. Two sides: what NOT to flag (recurring discards → less noise) and what they VALUE (what to prioritize / escalate).",
    edit:
      "Editable, additive. Add a rule when a client behavior is confirmed (e.g. \"don't flag sales/discounts\").",
  },
  {
    test: (rel) =>
      rel === "ARCHI.md" ||
      rel === "ARCHITECTURE.md" ||
      rel.startsWith("archi/"),
    title: "ARCHI.md",
    role:
      "The site map: URL structure, page templates, technical quirks found during reconnaissance.",
    edit:
      "Generated by the reconnaissance agent. Only fix it if you spot an error.",
  },
  {
    test: (rel) => rel === "history/SUMMARY.md",
    title: "history/SUMMARY.md",
    role:
      "Rolling summary injected into the prompt: the distilled state of what matters, in a few lines, to provide context without re-reading everything.",
    edit: "Managed by the agent at the end of a run. Manual edits are rare.",
  },
  {
    test: (rel) => rel.startsWith("history/traces/"),
    title: "Run trace",
    role:
      "Raw technical log of one execution (often large). Useful for debugging, not for everyday reading.",
    edit: "Read-only (append-only).",
  },
  {
    test: (rel) => /^history\/.+\.md$/.test(rel),
    title: "history/<date>.md",
    role:
      "A session journal: each finding, its verdict, and the discards with their reason. This is the dated episodic history.",
    edit: "Append-only in principle — avoid rewriting the past.",
  },
  {
    test: (rel) => rel === "skills/INDEX.md",
    title: "skills/INDEX.md",
    role: "Index of the custom skills available for this site.",
    edit: "Editable with care.",
  },
  {
    test: (rel) => rel.startsWith("skills/"),
    title: "Skill",
    role:
      "A skill: how to detect a specific class of problem on this site (a reusable procedure).",
    edit: "Editable — this is detection logic, treat it like code.",
  },
  {
    test: (rel) => rel === "zenyt-mapping.json",
    title: "zenyt-mapping.json",
    role:
      "Links this memory folder to the site's backend identifier (and environment).",
    edit: "Sensitive: only change it if you know what you're doing.",
  },
  {
    test: (rel) => rel.startsWith("scopes/"),
    title: "Scope",
    role: "Describes which URLs / which perimeter were scanned.",
    edit: "Machine-generated.",
  },
];

export function docForRel(rel) {
  if (!rel) return null;
  return FILE_DOCS.find((d) => d.test(rel)) || null;
}

export default FILE_DOCS;
